import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Ticket, Mail, X, CheckCheck } from "lucide-react";
import { apiUrl } from "../../utils/config";

const TYPE_ICON = {
  new_ticket:   <Ticket size={14} className="text-blue-500" />,
  ticket_reply: <Ticket size={14} className="text-orange-500" />,
  ticket_status:<Ticket size={14} className="text-green-500" />,
  new_contact:  <Mail size={14} className="text-purple-500" />,
};

const TYPE_NAV = {
  new_ticket:   "/admin/tickets",
  ticket_reply: "/admin/tickets",
  ticket_status:"/admin/tickets",
  new_contact:  "/admin/contacts",
};

const fmtTime = (d) => {
  if (!d) return "";
  const diff = (Date.now() - new Date(d)) / 1000;
  if (diff < 60)  return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short" });
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/notifications/admin`);
      if (res.data.status) {
        setNotifications(res.data.notifications);
        setUnread(res.data.unread);
      }
    } catch (_) {}
  }, []);

  // Poll every 30 seconds
  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (n) => {
    if (!n.is_read) {
      await axios.put(`${apiUrl}/notifications/${n.id}/read`).catch(() => {});
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnread(prev => Math.max(0, prev - 1));
    }
    setOpen(false);
    if (TYPE_NAV[n.type]) navigate(TYPE_NAV[n.type]);
  };

  const markAllRead = async () => {
    await axios.put(`${apiUrl}/notifications/read-all`, { recipient_type: "admin" }).catch(() => {});
    setNotifications(prev => prev.map(x => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await axios.delete(`${apiUrl}/notifications/${id}`).catch(() => {});
    setNotifications(prev => prev.filter(x => x.id !== id));
    setUnread(prev => {
      const wasUnread = notifications.find(x => x.id === id && !x.is_read);
      return wasUnread ? Math.max(0, prev - 1) : prev;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
        title="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-extrabold text-gray-900 text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-xs text-orange-500 font-bold hover:text-orange-600 transition-colors">
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : notifications.map(n => (
              <div
                key={n.id}
                onClick={() => markRead(n)}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${!n.is_read ? "bg-orange-50/60" : ""}`}
              >
                <div className="mt-0.5 flex-shrink-0">{TYPE_ICON[n.type] || <Bell size={14} className="text-gray-400" />}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${!n.is_read ? "font-semibold text-gray-900" : "text-gray-700"}`}>{n.title}</p>
                  {n.body && <p className="text-xs text-gray-400 mt-0.5 truncate">{n.body}</p>}
                  <p className="text-[10px] text-gray-400 mt-1">{fmtTime(n.created_at)}</p>
                </div>
                {!n.is_read && <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1.5" />}
                <button onClick={(e) => remove(e, n.id)} className="flex-shrink-0 p-0.5 text-gray-300 hover:text-gray-500 transition-colors">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
